import moment from 'moment';
import {
    EnabledChains,
    ORDER_SUBMIT_PENDING,
    OrderBookActionMapper,
    OrderBookActionTypes,
    OrderBookDomain,
    OrderBookParamMapper,
    OrderBookPathMapper,
    OrderBookType,
    OrderMessageType,
    SIGNATURE_PENDING,
} from '../../../config/constants';
import { ConstraintsStruct } from '../../../config/contracts/typechain/ITradeManager';
import { useWeb3React } from '@web3-react/core';
import {
    signTypedData,
    verifyChainConfig,
    priceFeedQuery,
    generatePendingTransactions,
    orderBookQuery,
    calculateLimitPrice,
} from '../../../helpers';
import { ethers } from 'ethers';
import { config } from '../../../config/config';
import { generatePath } from 'react-router-dom';
import useModalState from '../../../context/modal/useModalState';
import useUserOrders from './useUserOrders';
import useToastState from '../../../context/toast/useToastState';
import Button from '../../../components/Button';
import useUserPositions, { UserPositionRaw } from './useUserPositions';
import useTokenDetails from '../useTokenDetails';
import useTransactionState from '../../../context/transaction/useTransactionState';
import useUserFee from '../useUserFee';
import { TradePair } from '../../../context/chart/chartTypes';
import useUserSlippage from '../useUserSlippage';

export type CreateOrderOptions = {
    tradePair: TradePair;
    userPosition?: UserPositionRaw;
    limitPrice?: string;
    expiryDate?: Date;
    isMarketOrder?: boolean;
    type?: OrderBookType.TAKE_PROFIT | OrderBookType.STOP_LOSS;
};

const useCreateOrder = () => {
    const { openModal, setModalData } = useModalState();
    const { chainId, account, library } = useWeb3React();
    const { updateUserPositions } = useUserPositions();
    const { updateUserOrders } = useUserOrders();
    const { setToast } = useToastState();
    const { addPendingTransaction } = useTransactionState();
    const { userFee } = useUserFee();
    const userSlippage = useUserSlippage();

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const createOrder = async <
        T extends Exclude<
            OrderBookType,
            OrderBookType.LIQUIDATED | OrderBookType.TAKE_PROFIT | OrderBookType.STOP_LOSS
        >
    >(
        orderType: T,
        params: OrderBookParamMapper[T],
        primaryType: keyof (typeof OrderBookActionTypes)[T],
        isShort: boolean,
        getBankruptcyPrice: (price: string) => string,
        options: CreateOrderOptions = {
            tradePair: {} as TradePair,
            userPosition: undefined,
            expiryDate: undefined,
            limitPrice: undefined,
            type: undefined,
            isMarketOrder: undefined,
        }
    ) => {
        if (!account) throw new Error('No wallet connected');
        if (!verifyChainConfig(chainId)) throw new Error('Failed to verify chainID');
        if (!primaryType.toString().includes('Order'))
            throw new Error('Invalid primary type key specified');
        if (!decimals) throw new Error('Failed to retrieve decimals');
        if (!userFee) throw new Error('Failed to retrieve userFee');

        openModal('waitingModal');
        setModalData((prev) => ({ ...prev, status: SIGNATURE_PENDING }));

        const { limitPrice, expiryDate, type, isMarketOrder, tradePair, userPosition } =
            options;

        let price = limitPrice;
        let liquidationPrice;

        const latestPrice = await priceFeedQuery(params.tradePair.toString(), chainId);

        if (!price) {
            price = latestPrice;
            liquidationPrice = getBankruptcyPrice(latestPrice);
        }

        liquidationPrice = getBankruptcyPrice(price);

        const { minPrice, maxPrice } = calculateLimitPrice(
            isMarketOrder,
            type ?? orderType,
            isShort,
            price,
            parseFloat(userSlippage)
        );

        const constraints: ConstraintsStruct = {
            deadline: expiryDate
                ? moment(expiryDate).unix()
                : moment()
                      .add(1, isMarketOrder ? 'minute' : 'year')
                      .unix(),
            minPrice,
            maxPrice,
        };

        const message: OrderMessageType = {
            params,
            constraints,
            signatureHash: ethers.utils.keccak256(account),
            salt: moment().unix(),
        };

        const typedOrder = JSON.stringify({
            domain: OrderBookDomain(
                chainId,
                config.addresses[chainId as EnabledChains[number]].tradeManager
            ),
            message,
            primaryType,
            types: OrderBookActionTypes[orderType],
        });

        const signature = await signTypedData(library, account, typedOrder);

        setModalData((prev) => ({ ...prev, status: ORDER_SUBMIT_PENDING }));

        const path = generatePath(OrderBookPathMapper[orderType]);

        await orderBookQuery(
            path,
            {
                signature,
                maker: account,
                order: JSON.stringify(message),
                isShort,
                liquidationPrice,
                type,
                isMarketOrder,
            },
            { chainId }
        );

        if (isMarketOrder) {
            const pendingTransaction = generatePendingTransactions({
                decimals,
                isShort,
                message,
                orderType,
                price: latestPrice,
                sigHash: ethers.utils.keccak256(signature),
                tradePair,
                userFee,
                userPosition,
            });

            addPendingTransaction(pendingTransaction);
        }

        await updateUserOrders();

        // market order updates are handled within useOrderListener
        if (!isMarketOrder) {
            updateUserPositions();
        }

        const orderTypeLabel = isMarketOrder ? 'Market' : 'Limit';

        const orderMessage = type ? (
            `${OrderBookActionMapper[type]} set!`
        ) : (
            <>
                {orderType === OrderBookType.OPEN
                    ? `${orderTypeLabel} Order`
                    : `${OrderBookActionMapper[orderType]} ${orderTypeLabel}`}{' '}
                Submitted!&nbsp;
            </>
        );

        setToast(
            {
                type: 'primary',
                message: (
                    <>
                        {orderMessage}
                        {isMarketOrder && (
                            <Button
                                variant='transparent'
                                onClick={() => openModal('walletDropdown')}
                            >
                                View Log
                            </Button>
                        )}
                    </>
                ),
            },
            typedOrder
        );
    };

    return createOrder;
};

export default useCreateOrder;
